import React, { useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import { Icon } from "semantic-ui-react";
import _ from "lodash";
import { useSortable } from "@dnd-kit/sortable";
import ValueFilter from "../../Filters/ValueFilter";
import { itemAccessors } from "../../../../utils/constants";
import { setOrderTableAndMenuFilters } from "../../../../actions/ordersTable";

const STICKY_COLUMN_IDS = [itemAccessors.location, itemAccessors.code, itemAccessors.description];

const Header = ({ setFilter, tableWideFilteredRows, column, isSorted, isSortedDesc, setOrderTableAndMenuFilters, notificationFilterArray, isDropTarget, dropSide }) => {

  const isSticky = STICKY_COLUMN_IDS.includes(column.id);

  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: column.id,
    disabled: isSticky,
  });

  const { preFilteredRows, filteredRows } = column

  const filteredRowsKey = `${filteredRows.length}_${filteredRows[0] ? filteredRows[0].id : ''}`;
  const tableWideKey = `${tableWideFilteredRows.length}_${tableWideFilteredRows[0] ? tableWideFilteredRows[0].id : ''}`;

  //This use effect allow user to change color of notification filters
  //Here only send info to state
  useEffect(()=>{
    if(column.id === "location_code_concatenation"){
      const filterValues = filteredRows.map((filtervalue)=>(filtervalue.values.location_code_concatenation))
      if(!_.isEqual(filterValues, notificationFilterArray)){
        setOrderTableAndMenuFilters({[column.id]: filteredRows})
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRowsKey, column.id, setOrderTableAndMenuFilters, notificationFilterArray])

  const memoizedValueFilter = React.useMemo(() =>

    <ValueFilter
      columnId={column.id}
      setFilter={(values) => {
        setFilter(column.id, values)
      }}
      isFilterActive={preFilteredRows.length !== filteredRows.length}
      preFilteredRows={preFilteredRows}
      filteredRows={tableWideFilteredRows}
    />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      column.id,
      preFilteredRows,
      filteredRows.length,
      tableWideKey,
      setFilter,
    ]
  )

  const { style: headerStyle, ...restHeaderProps } = column.getHeaderProps();
  const sortToggleProps = column.getSortByToggleProps();

  return (
    <th
      ref={setNodeRef}
      key={column.id}
      className={classNames("order-table-header", {
        location: column.id === itemAccessors.location,
        code: column.id === itemAccessors.code,
        description: column.id === itemAccessors.description,
        variance: column.id === itemAccessors.orderVariance,
        "actual-order": column.id === itemAccessors.actualOrder,
        "suggested-order": column.id === itemAccessors.suggestedOrder,
        "is-dragging": isDragging,
        "drop-left": isDropTarget && dropSide === "left",
        "drop-right": isDropTarget && dropSide === "right",
      })}
      style={{
        ...(headerStyle || {}),
        cursor: isSticky ? "default" : (isDragging ? "grabbing" : "grab"),
      }}
      {...restHeaderProps}
      {...(isSticky ? {} : attributes)}
      {...(isSticky ? {} : listeners)}
    >
      <span
        className="column-header-title"
        onClick={sortToggleProps.onClick}
        onPointerDown={(e) => e.stopPropagation()}
        title={sortToggleProps.title}
        style={{ opacity: isDragging ? 0.4 : 1 }}
      >
        {column.render("Header")}
        {isSorted &&
          (isSortedDesc ? (
            <Icon name="caret down"></Icon>
          ) : (
              <Icon name="caret up"></Icon>
            ))}
      </span>
      <br />
      <span onPointerDown={(e) => e.stopPropagation()}>
        {memoizedValueFilter}
      </span>
    </th>
  );
};

Header.propTypes = {
  column: PropTypes.object,
  isSorted: PropTypes.bool,
  isSortedDesc: PropTypes.bool,
  setFilter: PropTypes.func,
  tableWideFilteredRows: PropTypes.array,
  setOrderTableAndMenuFilters: PropTypes.func,
  notificationFilterArray: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
  ]),
  isDropTarget: PropTypes.bool,
  dropSide: PropTypes.string,
};

const mapStateToProps = (state) => {
  return {
    notificationFilterArray: state.ordersTable.notificationFilterArray,
  };
};

export default connect(mapStateToProps, {setOrderTableAndMenuFilters})(React.memo(Header, (prevProps, nextProps) => {
  return (
    prevProps.setFilter === nextProps.setFilter &&
    prevProps.column.id === nextProps.column.id &&
    prevProps.column.Header === nextProps.column.Header &&
    prevProps.column.filteredRows.length === nextProps.column.filteredRows.length &&
    prevProps.column.preFilteredRows.length === nextProps.column.preFilteredRows.length &&
    prevProps.column.preFilteredRows === nextProps.column.preFilteredRows &&
    prevProps.isSorted === nextProps.isSorted &&
    prevProps.isSortedDesc === nextProps.isSortedDesc &&
    prevProps.tableWideFilteredRows.length === nextProps.tableWideFilteredRows.length &&
    (prevProps.tableWideFilteredRows[0] ? prevProps.tableWideFilteredRows[0].id : undefined) === (nextProps.tableWideFilteredRows[0] ? nextProps.tableWideFilteredRows[0].id : undefined) &&
    prevProps.notificationFilterArray === nextProps.notificationFilterArray &&
    prevProps.isDropTarget === nextProps.isDropTarget &&
    prevProps.dropSide === nextProps.dropSide
  );
}));
